import { Button, cx, Wordmark } from '@runnerboy/ui';
import { BadgeCheck, Bike, CalendarX2, IdCard, MapPinned, Store } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RoadNight } from '../components/art';
import { useSession } from '../lib/session';

const ONBOARDED_KEY = 'rb.onboarded';

export function isOnboarded(): boolean {
  try {
    return localStorage.getItem(ONBOARDED_KEY) === '1';
  } catch {
    return true;
  }
}

function Feature({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-white/10 text-marigold-500">{icon}</span>
      <span>
        <span className="block text-[15px] font-bold text-white">{title}</span>
        <span className="block text-sm leading-snug text-white/70">{body}</span>
      </span>
    </li>
  );
}

/** First run: the road-safety pledge, how Runner Boy works, and the legal-age confirmation. */
export function Onboarding({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const { setAgeGate } = useSession();
  const [step, setStep] = useState(0);
  const finish = (age: 'confirmed' | 'declined') => {
    setAgeGate(age);
    try {
      localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {
      /* ignore */
    }
    onDone();
  };
  const steps = 3;
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-night-900 text-white">
      <div className="rb-safe-top" />
      <div className="flex items-center justify-between px-5 pt-4">
        <Wordmark size="sm" inverted />
        {step < steps - 1 && (
          <button className="text-sm font-semibold text-white/70" onClick={() => setStep(steps - 1)}>
            {t('onboarding.skip', 'Skip')}
          </button>
        )}
      </div>

      <div className="relative flex-1 overflow-y-auto px-5">
        {step === 0 && (
          <div className="flex h-full flex-col">
            <div className="relative mt-4 overflow-hidden rounded-[24px]">
              <RoadNight className="h-[230px] w-full" />
            </div>
            <div className="mt-6 text-[11px] font-bold uppercase tracking-[0.2em] text-peacock-200">{t('safety.kicker', 'Runner Boy road-safety pledge')}</div>
            <h1 className="rb-display mt-2 text-[40px] font-extrabold leading-[0.95] text-marigold-500">{t('safety.line1', 'DON’T DRINK & DRIVE.')}</h1>
            <p className="rb-display mt-1 text-[26px] font-extrabold leading-[1.05]">{t('safety.line2', 'ORDER FROM YOUR PLACE. BE SAFE.')}</p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">{t('onboarding.s1', 'Runner Boy brings your neighbourhood licensed store to your door, so nobody has to drive after a drink.')}</p>
          </div>
        )}
        {step === 1 && (
          <div className="pt-8">
            <h2 className="rb-display text-[30px] font-extrabold leading-tight">{t('onboarding.s2title', 'How Runner Boy works')}</h2>
            <ul className="mt-6 flex flex-col gap-5">
              <Feature icon={<MapPinned className="h-6 w-6" />} title={t('onboarding.f1t', 'Choose your location')} body={t('onboarding.f1', 'We show the licensed stores that can legally deliver to you.')} />
              <Feature icon={<Store className="h-6 w-6" />} title={t('onboarding.f2t', 'Pick your store')} body={t('onboarding.f2', 'Its real shelf, its prices. The store is the seller; we deliver.')} />
              <Feature icon={<Bike className="h-6 w-6" />} title={t('onboarding.f3t', 'Track your runner live')} body={t('onboarding.f3', 'Watch your verified runner on the map, minute by minute.')} />
              <Feature icon={<BadgeCheck className="h-6 w-6" />} title={t('onboarding.f4t', 'Snacks, mixers, water & ice')} body={t('onboarding.f4', 'Everything that goes with it, in the same order.')} />
            </ul>
          </div>
        )}
        {step === 2 && (
          <div className="pt-8">
            <h2 className="rb-display text-[30px] font-extrabold leading-tight">{t('ageGate.title')}</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-white/75">{t('ageGate.body')}</p>
            <ul className="mt-6 flex flex-col gap-5">
              <Feature icon={<IdCard className="h-6 w-6" />} title={t('onboarding.f5t', 'ID checked at your door')} body={t('onboarding.f5', 'Keep an original ID ready. No ID, no alcohol.')} />
              <Feature icon={<CalendarX2 className="h-6 w-6" />} title={t('onboarding.f6t', 'Local rules respected')} body={t('onboarding.f6', 'Dry days, timings and limits are applied for you.')} />
            </ul>
          </div>
        )}
      </div>

      <div className="rb-safe-bottom px-5 pb-5 pt-3">
        <div className="mb-4 flex justify-center gap-1.5" aria-hidden>
          {Array.from({ length: steps }, (_, i) => (
            <span key={i} className={cx('h-1.5 rounded-full transition-all', i === step ? 'w-6 bg-marigold-500' : 'w-1.5 bg-white/25')} />
          ))}
        </div>
        {step < steps - 1 ? (
          <Button size="lg" block variant="accent" onClick={() => setStep(step + 1)}>
            {t('onboarding.next', 'Continue')}
          </Button>
        ) : (
          <div className="flex flex-col gap-2">
            <Button size="lg" block variant="accent" onClick={() => finish('confirmed')}>
              {t('ageGate.confirm')}
            </Button>
            <Button size="lg" block variant="ghost" className="text-white hover:bg-white/10" onClick={() => finish('declined')}>
              {t('ageGate.decline')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
